import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Rss } from "lucide-react";
import { FaTiktok } from "react-icons/fa6";
import { getRssFeedUrl } from "@/config/seo.config";
import endsofttechLogo from "@/assets/endsofttech.webp";
import trustMarkBadge from "@/assets/Trusted.webp";
import dpoDpsMark from "@/assets/dpo-dps-mark.webp";

const Footer = () => {
  const rssFeedUrl = getRssFeedUrl();

  // Top Right Links
  const quickLinks = [
    { name: "Home", path: "/" }, // Set to "/"
    { name: "Resources", path: "/resources" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Careers", path: "/careers" },
  ];

  // Bottom Center Links
  const bottomCenterLinks = [
    {
      name: "About Endsofttech",
      url: "https://endsofttech.com/about/",
      type: "external",
    },
    {
      name: "EWS Hosting Products",
      url: "https://ewshosting.net/",
      type: "external",
    },
    { name: "Privacy", path: "/privacy", type: "internal" },
    { name: "Terms", path: "/terms", type: "internal" },
  ];

  // --- NEW HELPER FUNCTION ---
  // This forces the page to scroll to the top when a link is clicked
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Use "auto" if you want instant scrolling
    });
  };

  return (
    <footer className="overflow-hidden border-t border-gray-100 bg-white pt-8 pb-6 font-sans md:pt-12">
      <div className="mx-auto flex w-[90%] flex-col justify-between md:w-[92%]">
        {/* --- TOP SECTION --- */}
        <div className="mb-8 flex flex-col items-start justify-between gap-8 md:mb-12 md:flex-row md:gap-0">
          {/* Left: Tagline */}
          <div className="max-w-xs md:max-w-md">
            <h3 className="text-2xl leading-tight font-medium text-gray-900 md:text-3xl">
              Streamline your
              <br />
              veterinary practice.
            </h3>
          </div>

          {/* Right: Quick Links */}
          <div className="w-full text-left md:w-auto md:text-right">
            <ul className="flex flex-col items-start space-y-3 md:items-end md:space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={handleScrollToTop} // <--- ADDED HERE
                    className="block text-base font-medium text-gray-600 transition-colors hover:text-gray-900">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* --- MIDDLE SECTION: GIANT TEXT --- */}
        <div className="pointer-events-none relative z-10 w-full border-b border-transparent leading-none select-none">
          <h1 className="flex w-full justify-between text-[13.5vw] leading-[0.75] font-bold tracking-tighter text-gray-900 uppercase md:text-[14.5vw]">
            <span>V</span>
            <span>E</span>
            <span>T</span>
            <span className="w-[1vw]"></span>
            <span>A</span>
            <span>S</span>
            <span>S</span>
            <span>I</span>
            <span>S</span>
            <span>T</span>
          </h1>
        </div>

        {/* --- BOTTOM SECTION --- */}
        <div className="mt-8 flex flex-col items-center justify-between gap-8 lg:mt-12 lg:flex-row lg:gap-0">
          {/* 1. Bottom Left: LOGOS */}
          <div className="order-2 flex shrink-0 flex-row flex-wrap items-center justify-center gap-4 lg:order-1 lg:flex-nowrap lg:gap-8">
            <a
              href="https://endsofttech.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block">
              <img
                src={endsofttechLogo}
                alt="Endsofttech"
                className="h-14 w-auto object-contain sm:h-16 lg:h-32"
              />
            </a>
            <img
              src={trustMarkBadge}
              alt="TrustMark Registered"
              className="h-14 w-auto object-contain sm:h-16 lg:h-28"
            />
            <img
              src={dpoDpsMark}
              alt="DPO-DPS Mark"
              className="h-14 w-auto object-contain sm:h-16 lg:h-28"
            />
          </div>

          {/* 2. Bottom Right: Links (Shifted Left) */}
          <div className="order-1 flex w-full flex-col items-center gap-4 text-center lg:order-2 lg:mr-24 lg:w-auto lg:items-end">
            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/vetassist"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 transition-colors hover:text-gray-700">
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/VetAssistApp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 transition-colors hover:text-gray-700">
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/vet_assist_app/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 transition-colors hover:text-gray-700">
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@vetassistapp?lang=en"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 transition-colors hover:text-gray-700"
                aria-label="TikTok">
                <FaTiktok className="h-5 w-5" />
              </a>
              <a
                href={rssFeedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 transition-colors hover:text-gray-700"
                aria-label="RSS Feed">
                <Rss className="h-5 w-5" />
              </a>
            </div>
            {/* Bottom Links */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-3 text-sm font-medium text-gray-500 lg:gap-x-8 lg:text-base">
              {bottomCenterLinks.map((link) => (
                <React.Fragment key={link.name}>
                  {link.type === "external" ? (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap transition-colors hover:text-gray-900">
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={handleScrollToTop} // <--- ADDED HERE AS WELL
                      className="whitespace-nowrap transition-colors hover:text-gray-900">
                      {link.name}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
